import { compactNumber } from "@/lib/formatNumber";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePagination } from "@/lib/usePagination";
import { resolveMediaUrl } from "@/components/FileUpload";
import { imageVariant } from "@/lib/mediaUrl";
import { denominationLabel } from "@/lib/denominations";

const NO_DENOM = "__none__";
import ImageCropUpload, { CoverEditMenu } from "@/components/ImageCropUpload";
import { CardGridSkeleton } from "@/components/FeedSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogBody } from "@/components/ui/dialog";
import { Users, Plus, Compass, Search, Globe, Lock, ImagePlus, ArrowRight, CheckCircle2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const groupGradient = (id = "") => {
  const grads = [
    "linear-gradient(135deg,#D97757,#E0A63C)",
    "linear-gradient(135deg,#2E7CF6,#63b3ff)",
    "linear-gradient(135deg,#2D9E68,#7ed3a6)",
    "linear-gradient(135deg,#8B5CF6,#c4b5fd)",
    "linear-gradient(135deg,#0D9488,#5eead4)",
    "linear-gradient(135deg,#E0475F,#f7a1ad)",
  ];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % grads.length;
  return grads[h];
};

const GroupCover = ({ g, className = "h-32" }) => {
  const { t } = useApp();
  return (
  <div className={`relative w-full ${className} overflow-hidden`}>
    {g.cover_url ? (
      <img src={imageVariant(g.cover_url, 640)} alt="" className="absolute inset-0 w-full h-full object-cover" />
    ) : (
      <div className="absolute inset-0" style={{ background: groupGradient(g.id) }} />
    )}
    <div className="absolute inset-0 bg-black/10" />
    {g.privacy === "private" && (
      <span className="absolute top-2 right-2 chip text-[10px]" style={{ background: "rgba(0,0,0,0.55)", color: "white", borderColor: "transparent" }}>
        <Lock size={10} className="inline mr-1" /> {t("groups.private", "Privé")}
      </span>
    )}
  </div>
  );
};

const GroupCard = ({ g, onOpen, onJoin }) => {
  const { t } = useApp();
  return (
  <div className="yalola-card yalola-card-hover overflow-hidden flex flex-col" data-testid={`group-card-${g.id}`}>
    <button className="block w-full text-left" onClick={() => onOpen(g)} data-testid={`group-open-${g.id}`}>
      <GroupCover g={g} />
    </button>
    <div className="p-4 flex flex-col flex-1">
      <h3 className="font-semibold text-[15px] truncate" style={{ fontFamily: "Outfit" }}>{g.name}</h3>
      <p className="mt-1 text-sm line-clamp-2 min-h-[2.4em]" style={{ color: "var(--yalola-text-muted)" }}>{g.description || "—"}</p>
      <div className="flex items-center gap-1.5 mt-2 text-xs" style={{ color: "var(--yalola-text-muted)" }}>
        <Users size={12} /> {compactNumber(g.member_count)} {t("groups.members", "membre(s)")}
        {g.denomination && <span className="chip ml-1">{denominationLabel(t, g.denomination)}</span>}
      </div>
      <div className="mt-auto pt-3">
        {g.is_member ? (
          <button onClick={() => onOpen(g)} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`group-enter-${g.id}`}>
            {t("groups.open", "Ouvrir")} <ArrowRight size={14} />
          </button>
        ) : (
          <Button size="sm" className="rounded-full pill-primary w-full justify-center" onClick={(e) => onJoin(g, e)} data-testid={`group-join-${g.id}`}>
            {t("groups.join", "Rejoindre")}
          </Button>
        )}
      </div>
    </div>
  </div>
  );
};

export const Groups = () => {
  const nav = useNavigate();
  const { user, t, config } = useApp();
  // Admin-managed list (Admin → Obédiences); the same source as the profile form.
  const denoms = useMemo(() => (config?.denominations?.length ? config.denominations.map((d) => d.name) : []), [config]);
  const [tab, setTab] = useState("mine");
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", denomination: "", privacy: "public", cover_url: "" });

  useEffect(() => { const id = setTimeout(() => setDq(q), 350); return () => clearTimeout(id); }, [q]);

  const fetcher = useCallback(async (skip, limit) => {
    const qs = new URLSearchParams({ skip, limit });
    if (tab === "discover" && dq.trim()) qs.set("search", dq.trim());
    const { data } = await api.get(`/groups?${qs.toString()}`);
    return { items: data.groups || [], hasMore: data.has_more };
  }, [tab, dq]);
  const { items: groups, loading, loadingMore, hasMore, sentinelRef } = usePagination(fetcher, { limit: 24, deps: [tab, dq] });

  const mine = useMemo(() => groups.filter((g) => g.is_member), [groups]);
  const discover = useMemo(() => groups.filter((g) => !g.is_member), [groups]);

  const resetForm = () => setForm({ name: "", description: "", denomination: "", privacy: "public", cover_url: "" });

  const create = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) { toast.error(t("groups.err_name", "Donne un nom d'au moins 2 caractères")); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/groups", form);
      toast.success(t("groups.created", "Groupe créé ! 🎉"));
      setOpen(false);
      resetForm();
      nav(`/groups/${data.group.id}`);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    setCreating(false);
  };

  const openGroup = (g) => nav(`/groups/${g.id}`);
  const join = async (g, e) => {
    e?.stopPropagation?.();
    try {
      const { data } = await api.post(`/groups/${g.id}/join`);
      if (data.pending) { toast.success(t("groups.join_pending", "Demande envoyée — en attente de validation par un administrateur du groupe.")); return; }
      toast.success(`${t("groups.welcome", "Bienvenue dans")} ${g.name} !`);
      nav(`/groups/${g.id}`);
    } catch { toast.error(t("common.error", "Erreur")); }
  };

  const NAV = [
    { key: "mine", label: t("groups.tab_mine", "Mes groupes"), icon: Users, count: mine.length },
    { key: "discover", label: t("groups.tab_discover", "Découvrir"), icon: Compass, count: discover.length },
  ];
  const list = tab === "mine" ? mine : discover;

  const CreateButton = user && (
    <Button className="pill-primary rounded-full w-full" onClick={() => setOpen(true)} data-testid="create-group-btn">
      <Plus size={16} className="mr-1" /> {t("groups.create", "Créer un groupe")}
    </Button>
  );

  return (
    <AppShell showSidebars={false}>
      <div className="grid lg:grid-cols-[260px_1fr] gap-6" data-testid="groups-page">
        {/* Left rail */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 space-y-4">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "#2E7CF61f" }}><Users size={18} color="#2E7CF6" /></span>
              <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{t("groups.title", "Groupes")}</h1>
            </div>
            {CreateButton}
            <div className="yalola-card p-2">
              {NAV.map((n) => {
                const active = tab === n.key;
                return (
                  <button key={n.key} onClick={() => setTab(n.key)} className="w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]" style={{ background: active ? "var(--yalola-primary-soft)" : "transparent" }} data-testid={`groups-tab-${n.key}`}>
                    <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: active ? "var(--yalola-primary)" : "var(--yalola-surface-alt)" }}>
                      <n.icon size={16} color={active ? "#fff" : "var(--yalola-text)"} />
                    </span>
                    <span className="text-sm flex-1 text-left" style={{ fontWeight: active ? 700 : 500, color: active ? "var(--yalola-primary)" : "var(--yalola-text)" }}>{n.label}</span>
                    <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{n.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 space-y-4">
          {/* Mobile bar */}
          <div className="lg:hidden space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{t("groups.title", "Groupes")}</h1>
              {user && <Button className="pill-primary rounded-full shrink-0" onClick={() => setOpen(true)} data-testid="create-group-btn-mobile"><Plus size={16} /></Button>}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {NAV.map((n) => (
                <button key={n.key} onClick={() => setTab(n.key)} className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border" style={{ background: tab === n.key ? "var(--yalola-primary)" : "transparent", color: tab === n.key ? "#fff" : "var(--yalola-text)", borderColor: tab === n.key ? "transparent" : "var(--yalola-border)" }} data-testid={`groups-tab-mobile-${n.key}`}>
                  {n.label} ({n.count})
                </button>
              ))}
            </div>
          </div>

          {tab === "discover" && (
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
              <Input placeholder={t("groups.search_ph", "Rechercher un groupe…")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full" data-testid="groups-search-input" />
            </div>
          )}

          {loading && <CardGridSkeleton count={6} />}

          {!loading && list.length === 0 && (
            <div className="yalola-card p-10 text-center" data-testid={tab === "mine" ? "groups-mine-empty" : "groups-discover-empty"}>
              <span className="mx-auto mb-3 h-14 w-14 rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}>
                <Users size={26} style={{ color: "var(--yalola-primary)" }} />
              </span>
              {tab === "mine" ? (
                <>
                  <h3 className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>{t("groups.mine_empty_title", "Tu n'as encore rejoint aucun groupe")}</h3>
                  <p className="mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("groups.mine_empty_body", "Explore les groupes existants ou crée le tien pour rassembler ta communauté.")}</p>
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button variant="outline" className="rounded-full" onClick={() => setTab("discover")} data-testid="goto-discover-btn"><Compass size={15} className="mr-1" /> {t("groups.discover_cta", "Découvrir des groupes")}</Button>
                  </div>
                </>
              ) : (
                <p style={{ color: "var(--yalola-text-muted)" }}>{q ? t("groups.no_match", "Aucun groupe ne correspond à ta recherche.") : t("groups.discover_empty", "Aucun autre groupe pour l'instant. Sois le premier à en créer un !")}</p>
              )}
            </div>
          )}

          {!loading && list.length > 0 && (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {list.map((g) => <GroupCard key={g.id} g={g} onOpen={openGroup} onJoin={join} />)}
            </div>
          )}

          {!loading && hasMore && (
            <div ref={sentinelRef} className="py-6 grid place-items-center" data-testid="groups-sentinel">
              {loadingMore && <Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} />}
            </div>
          )}
        </main>
      </div>

      {/* Guided create flow */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent variant="sheet">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>{t("groups.create", "Créer un groupe")}</DialogTitle>
            <DialogDescription>{t("groups.create_desc", "Un groupe rassemble des membres autour d'un thème. Suis les étapes ci-dessous.")}</DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            {/* Cover */}
            <div>
              <Label className="text-sm font-semibold">{t("groups.cover_label", "Photo de couverture")} <span className="font-normal" style={{ color: "var(--yalola-text-muted)" }}>{t("common.optional", "(optionnel)")}</span></Label>
              <div className="mt-1.5 relative rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }}>
                <div className="h-28 relative">
                  {form.cover_url ? (
                    <img src={resolveMediaUrl(form.cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="group-cover-preview" />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center" style={{ background: groupGradient(form.name || "y") }}><ImagePlus size={26} className="text-white/80" /></div>
                  )}
                  <div className="absolute bottom-2 right-2 flex gap-2">
                    {form.cover_url ? (
                      <CoverEditMenu currentUrl={form.cover_url} aspect={3} onUploaded={(url) => setForm((f) => ({ ...f, cover_url: url }))} onRemove={() => setForm((f) => ({ ...f, cover_url: "" }))} testId="group-cover-edit" />
                    ) : (
                      <ImageCropUpload aspect={3} icon={ImagePlus} buttonLabel={t("common.add", "Ajouter")} title={t("groups.cover_adjust", "Ajuster la couverture")} onUploaded={(url) => setForm((f) => ({ ...f, cover_url: url }))} testId="group-cover-upload" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">{t("groups.name_label", "Nom du groupe *")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("groups.name_ph", "Ex : Jeunesse en prière")} maxLength={80} className="mt-1.5" data-testid="group-name-input" />
              <p className="mt-1 text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("groups.name_hint", "Choisis un nom clair et accueillant.")}</p>
            </div>

            <div>
              <Label className="text-sm font-semibold">{t("common.description", "Description")}</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("groups.desc_ph", "De quoi parle ce groupe ? Qui peut le rejoindre ?")} className="mt-1.5" data-testid="group-desc-input" />
            </div>

            <div>
              <Label className="text-sm font-semibold">{t("groups.denom_label", "Dénomination")} <span className="font-normal" style={{ color: "var(--yalola-text-muted)" }}>{t("common.optional", "(optionnel)")}</span></Label>
              <Select value={form.denomination || NO_DENOM} onValueChange={(v) => setForm({ ...form, denomination: v === NO_DENOM ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="group-denom-input"><SelectValue placeholder={t("profile_edit.choose_ph", "Choisir...")} /></SelectTrigger>
                <SelectContent className="max-h-64" data-testid="group-denom-options">
                  <SelectItem value={NO_DENOM}>{t("groups.denom_none", "Toutes / non précisée")}</SelectItem>
                  {denoms.map((d) => <SelectItem key={d} value={d} data-testid={`group-denom-option-${d}`}>{denominationLabel(t, d)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold">{t("groups.privacy_label", "Confidentialité")}</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {[
                  { key: "public", icon: Globe, title: t("groups.privacy_public", "Public"), desc: t("groups.privacy_public_desc", "Tout le monde peut voir et rejoindre.") },
                  { key: "private", icon: Lock, title: t("groups.privacy_private", "Privé"), desc: t("groups.privacy_private_desc", "Réservé aux membres ; posts non partageables.") },
                ].map((opt) => {
                  const active = form.privacy === opt.key;
                  return (
                    <button key={opt.key} type="button" onClick={() => setForm({ ...form, privacy: opt.key })} className="relative rounded-xl border p-3 text-left transition-colors" style={{ borderColor: active ? "var(--yalola-primary)" : "var(--yalola-border)", background: active ? "var(--yalola-primary-soft)" : "transparent" }} data-testid={`group-privacy-${opt.key}`}>
                      {active && <CheckCircle2 size={16} className="absolute top-2 right-2" style={{ color: "var(--yalola-primary)" }} />}
                      <opt.icon size={18} style={{ color: active ? "var(--yalola-primary)" : "var(--yalola-text)" }} />
                      <div className="mt-1 text-sm font-semibold">{opt.title}</div>
                      <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }} data-testid="group-create-cancel">{t("common.cancel", "Annuler")}</Button>
            <Button className="pill-primary" onClick={create} disabled={creating || form.name.trim().length < 2} data-testid="group-create-submit">
              {creating ? t("groups.creating", "Création…") : t("groups.create_submit", "Créer le groupe")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default Groups;
